<script setup lang="ts">
import { computed } from 'vue'
import RecoveryMethodPicker from './RecoveryMethodPicker.vue'
import { useRecoveryConfig, type RecoveryException } from './useRecoveryConfig'

const { t } = useI18n()

defineProps<{
  isOpen: boolean
  isEditing: boolean
}>()

const draft = defineModel<RecoveryException>('draft', { required: true })

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save'): void
}>()

const { stores } = useLocalStorage()
const { isSelectionValid, storeIdsTakenByOtherExceptions } = useRecoveryConfig()

const allowedStores = computed(() => {
  const taken = storeIdsTakenByOtherExceptions(draft.value.id)
  return stores.value.filter((s) => !taken.includes(s.id))
})

const canSave = computed(() => draft.value.storeIds.length >= 1 && isSelectionValid(draft.value.selection))
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="
      isEditing
        ? t('pages.loyaltyPage.tabs.recovery.exceptionModal.editTitle')
        : t('pages.loyaltyPage.tabs.recovery.exceptionModal.addTitle')
    "
    size="4xl"
    :cancel="t('global.words.cancel')"
    :link-text="isEditing ? t('global.words.save') : t('global.words.add')"
    :is-disabled="!canSave"
    @close="emit('close')"
    @click="emit('save')"
  >
    <div class="space-y-8 px-2 py-2">
      <div class="space-y-2">
        <label class="block text-sm font-medium text-foreground">
          {{ t('pages.loyaltyPage.tabs.recovery.exceptionModal.storesLabel') }}
        </label>
        <MultiSelectStores
          v-model="draft.storeIds"
          :has-label="false"
          :stores-with-permission="allowedStores"
          class="sm:w-full"
        />
        <p class="text-xs text-muted-foreground">
          {{ t('pages.loyaltyPage.tabs.recovery.exceptionModal.storesHelp') }}
        </p>
      </div>

      <div class="space-y-3">
        <label class="block text-sm font-medium text-foreground">
          {{ t('pages.loyaltyPage.tabs.recovery.exceptionModal.methodsLabel') }}
        </label>
        <RecoveryMethodPicker v-model="draft.selection" />
      </div>
    </div>
  </BaseModal>
</template>
